import {
    Badge,
    Box,
    Button,
    Divider,
    HStack,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    SimpleGrid,
    Spinner,
    Stat,
    StatHelpText,
    StatLabel,
    StatNumber,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    VStack,
    useToast,
} from "@chakra-ui/react";
import { SearchIcon, RepeatIcon } from "@chakra-ui/icons";
import { useCallback, useEffect, useState } from "react";

const REASON_LABELS = {
    wrong_password: "Senha incorreta",
    user_not_found: "Usuário não encontrado",
    network_denied: "Acesso de rede negado",
    password_reset_pending: "Redefinição de senha pendente",
};

const formatDateTime = (value) => {
    if (!value) return "—";
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleString("pt-BR", {
        timeZone: "America/Sao_Paulo",
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const formatRelative = (value) => {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    const diff = Date.now() - date.getTime();
    if (diff < 0) return null;
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return "agora mesmo";
    if (minutes < 60) return `há ${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `há ${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `há ${days}d`;
    const months = Math.floor(days / 30);
    if (months < 12) return `há ${months} meses`;
    return `há ${Math.floor(months / 12)} anos`;
};

const AccessLogsTab = () => {
    const [logs, setLogs] = useState([]);
    const [stats, setStats] = useState(null);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const limit = 50;

    const [filters, setFilters] = useState({
        search: "",
        success: "",
        isInternal: "",
        startDate: "",
        endDate: "",
    });

    const toast = useToast();

    const fetchStats = useCallback(async () => {
        try {
            const token = localStorage.getItem("token");
            const res = await fetch("/api/access-logs/stats", {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error();
            const data = await res.json();
            setStats(data.summary);
        } catch {
            // silent
        }
    }, []);

    const fetchLogs = useCallback(async (currentPage = 0, currentFilters = filters) => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const params = new URLSearchParams({
                limit,
                offset: currentPage * limit,
            });
            if (currentFilters.search) params.set("search", currentFilters.search);
            if (currentFilters.success !== "") params.set("success", currentFilters.success);
            if (currentFilters.isInternal !== "") params.set("isInternal", currentFilters.isInternal);
            if (currentFilters.startDate) params.set("startDate", currentFilters.startDate);
            if (currentFilters.endDate) params.set("endDate", currentFilters.endDate);

            const res = await fetch(`/api/access-logs?${params}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Erro ao buscar logs");
            const data = await res.json();
            setLogs(data.logs);
            setTotal(data.total);
        } catch (error) {
            toast({ title: "Erro ao carregar logs", status: "error", duration: 3000, isClosable: true });
        } finally {
            setLoading(false);
        }
    }, [filters, toast]);

    useEffect(() => {
        fetchStats();
        fetchLogs(0, filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = () => {
        setPage(0);
        fetchLogs(0, filters);
    };

    const handleReset = () => {
        const cleared = { search: "", success: "", isInternal: "", startDate: "", endDate: "" };
        setFilters(cleared);
        setPage(0);
        fetchLogs(0, cleared);
        fetchStats();
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
        fetchLogs(newPage, filters);
    };

    const totalPages = Math.ceil(total / limit);

    return (
        <Box>
            {stats && (
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
                    <Box bg="white" p={4} borderRadius="md" boxShadow="sm">
                        <Stat>
                            <StatLabel>Logins hoje</StatLabel>
                            <StatNumber color="green.500">{stats.today_success || 0}</StatNumber>
                            <StatHelpText>{stats.today_failure || 0} falhas</StatHelpText>
                        </Stat>
                    </Box>
                    <Box bg="white" p={4} borderRadius="md" boxShadow="sm">
                        <Stat>
                            <StatLabel>Últimos 7 dias</StatLabel>
                            <StatNumber color="blue.500">{stats.week_success || 0}</StatNumber>
                            <StatHelpText>{stats.week_failure || 0} falhas</StatHelpText>
                        </Stat>
                    </Box>
                    <Box bg="white" p={4} borderRadius="md" boxShadow="sm">
                        <Stat>
                            <StatLabel>Total de logins</StatLabel>
                            <StatNumber>{stats.total_success || 0}</StatNumber>
                            <StatHelpText>{stats.total_failure || 0} falhas totais</StatHelpText>
                        </Stat>
                    </Box>
                    <Box bg="white" p={4} borderRadius="md" boxShadow="sm">
                        <Stat>
                            <StatLabel>Usuários únicos</StatLabel>
                            <StatNumber color="purple.500">{stats.unique_users || 0}</StatNumber>
                            <StatHelpText>com login bem-sucedido</StatHelpText>
                        </Stat>
                    </Box>
                </SimpleGrid>
            )}

            <Box bg="white" p={4} borderRadius="md" boxShadow="sm" mb={4}>
                <HStack spacing={3} flexWrap="wrap">
                    <InputGroup maxW="220px">
                        <InputLeftElement pointerEvents="none">
                            <SearchIcon color="gray.400" />
                        </InputLeftElement>
                        <Input
                            placeholder="Buscar usuário"
                            value={filters.search}
                            onChange={(e) => setFilters((current) => ({ ...current, search: e.target.value }))}
                            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                        />
                    </InputGroup>
                    <Select
                        maxW="200px"
                        value={filters.success}
                        onChange={(e) => setFilters((current) => ({ ...current, success: e.target.value }))}
                    >
                        <option value="">Todos os resultados</option>
                        <option value="true">Bem-sucedidos</option>
                        <option value="false">Falhas</option>
                    </Select>
                    <Select
                        maxW="180px"
                        value={filters.isInternal}
                        onChange={(e) => setFilters((current) => ({ ...current, isInternal: e.target.value }))}
                    >
                        <option value="">Todas as redes</option>
                        <option value="true">Interna</option>
                        <option value="false">Externa</option>
                    </Select>
                    <Input
                        type="date"
                        maxW="170px"
                        value={filters.startDate}
                        onChange={(e) => setFilters((current) => ({ ...current, startDate: e.target.value }))}
                        placeholder="Data início"
                    />
                    <Input
                        type="date"
                        maxW="170px"
                        value={filters.endDate}
                        onChange={(e) => setFilters((current) => ({ ...current, endDate: e.target.value }))}
                        placeholder="Data fim"
                    />
                    <Button colorScheme="teal" onClick={handleSearch}>
                        Filtrar
                    </Button>
                    <Tooltip label="Limpar filtros e recarregar">
                        <IconButton icon={<RepeatIcon />} onClick={handleReset} aria-label="Reset" />
                    </Tooltip>
                </HStack>
            </Box>

            <Box bg="white" borderRadius="md" boxShadow="sm" overflowX="auto">
                {loading ? (
                    <VStack py={12}>
                        <Spinner size="xl" color="teal.500" />
                        <Text color="gray.500">Carregando...</Text>
                    </VStack>
                ) : (
                    <>
                        <Table variant="simple" size="sm">
                            <Thead bg="gray.50">
                                <Tr>
                                    <Th>Usuário</Th>
                                    <Th>Data/Hora (Brasília)</Th>
                                    <Th>Última requisição</Th>
                                    <Th>IP</Th>
                                    <Th>Rede</Th>
                                    <Th>Resultado</Th>
                                    <Th>Motivo da falha</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {logs.length === 0 ? (
                                    <Tr>
                                        <Td colSpan={7} textAlign="center" py={8} color="gray.400">
                                            Nenhum registro encontrado
                                        </Td>
                                    </Tr>
                                ) : (
                                    logs.map((log) => {
                                        const lastAccessRelative = formatRelative(log.lastAccessAt);

                                        return (
                                            <Tr key={log.id} _hover={{ bg: "gray.50" }}>
                                                <Td>
                                                    <VStack align="start" spacing={0}>
                                                        <Text fontWeight="medium" fontSize="sm">{log.username}</Text>
                                                        {log.userRole && (
                                                            <Text fontSize="xs" color="gray.500">Nível {log.userRole}</Text>
                                                        )}
                                                    </VStack>
                                                </Td>
                                                <Td whiteSpace="nowrap" fontSize="xs">
                                                    {formatDateTime(log.loginAt)}
                                                </Td>
                                                <Td>
                                                    <Tooltip label={lastAccessRelative || ""} isDisabled={!lastAccessRelative} openDelay={300}>
                                                        <Text fontSize="xs" color={log.lastAccessAt ? "gray.700" : "gray.400"} whiteSpace="nowrap">
                                                            {formatDateTime(log.lastAccessAt)}
                                                        </Text>
                                                    </Tooltip>
                                                </Td>
                                                <Td>
                                                    <Text fontSize="xs" fontFamily="mono">{log.ip || "—"}</Text>
                                                </Td>
                                                <Td>
                                                    {log.isInternal === null ? (
                                                        <Text fontSize="xs" color="gray.400">—</Text>
                                                    ) : log.isInternal ? (
                                                        <Badge colorScheme="blue" fontSize="xs">Interna</Badge>
                                                    ) : (
                                                        <Badge colorScheme="orange" fontSize="xs">Externa</Badge>
                                                    )}
                                                </Td>
                                                <Td>
                                                    {log.success ? (
                                                        <Badge colorScheme="green">Sucesso</Badge>
                                                    ) : (
                                                        <Badge colorScheme="red">Falha</Badge>
                                                    )}
                                                </Td>
                                                <Td>
                                                    <Text fontSize="xs" color="gray.600">
                                                        {log.reason ? (REASON_LABELS[log.reason] || log.reason) : "—"}
                                                    </Text>
                                                </Td>
                                            </Tr>
                                        );
                                    })
                                )}
                            </Tbody>
                        </Table>

                        {totalPages > 1 && (
                            <>
                                <Divider />
                                <HStack justify="space-between" p={3}>
                                    <Text fontSize="sm" color="gray.500">
                                        Exibindo {page * limit + 1}–{Math.min((page + 1) * limit, total)} de {total} registros
                                    </Text>
                                    <HStack>
                                        <Button
                                            size="sm"
                                            onClick={() => handlePageChange(page - 1)}
                                            isDisabled={page === 0}
                                        >
                                            Anterior
                                        </Button>
                                        <Text fontSize="sm">
                                            {page + 1} / {totalPages}
                                        </Text>
                                        <Button
                                            size="sm"
                                            onClick={() => handlePageChange(page + 1)}
                                            isDisabled={page >= totalPages - 1}
                                        >
                                            Próxima
                                        </Button>
                                    </HStack>
                                </HStack>
                            </>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
};

export default AccessLogsTab;